import { EnqueueSnackbarOptions } from 'hooks/useSnackbar';

type EnqueueSnackbar = (message: string, options?: EnqueueSnackbarOptions) => void;
type TranslateFunction = (key: string, options?: Record<string, string>) => string;

export type CohortOperation =
  | 'createCohort'
  | 'deleteCohort'
  | 'executeCohort'
  | 'cancelExecution'
  | 'retrySiteExecution'
  | 'sendNotifications'
  | 'sendReminders'
  | 'updateDowntime'
  | 'addSite'
  | 'removeSite'
  | 'assignSites'
  | 'loadCohorts'
  | 'loadCohortDetail'
  | 'loadSites'
  | 'searchSites'
  | 'generateReport';

interface HandleApiErrorOptions {
  operation: CohortOperation;
  enqueueSnackbar: EnqueueSnackbar;
  t: TranslateFunction;
  entityName?: string;
}

const isAbortError = (error: any): boolean =>
  error?.name === 'AbortError' || error?.message === 'Cancel';

const isNetworkError = (error: any): boolean =>
  !error?.response && (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK');

const isTimeoutError = (error: any): boolean =>
  error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');

export const handleApiError = (error: any, options: HandleApiErrorOptions): void => {
  const { operation, enqueueSnackbar, t, entityName } = options;

  if (isAbortError(error)) return;

  const status = error?.response?.status;
  const operationLabel = t(`cohorts.errors.operations.${operation}`);

  if (status === 401) {
    enqueueSnackbar(t('cohorts.errors.sessionExpired'), { variant: 'error' });
    return;
  }

  if (status === 403) {
    enqueueSnackbar(t('cohorts.errors.permissionDenied', { action: operationLabel }), {
      variant: 'error',
    });
    return;
  }

  if (status === 429) {
    enqueueSnackbar(t('cohorts.errors.rateLimited'), { variant: 'warning' });
    return;
  }

  if (isNetworkError(error)) {
    enqueueSnackbar(t('cohorts.errors.networkError'), { variant: 'error' });
    return;
  }

  if (isTimeoutError(error)) {
    enqueueSnackbar(t('cohorts.errors.timeout', { action: operationLabel }), { variant: 'error' });
    return;
  }

  const serverMessage =
    error?.response?.data?.message || error?.response?.data?.error || error?.message || '';

  enqueueSnackbar(
    t(`cohorts.errors.${operation}`, {
      message: serverMessage,
      name: entityName || '',
    }),
    { variant: 'error' },
  );
};

export const isBusinessError = (error: any, status: number, errorType?: string): boolean => {
  if (error?.response?.status !== status) return false;
  if (errorType && error?.response?.data?.error_type !== errorType) return false;
  return true;
};
